import {useCallback} from 'react';
import type {WorkV4} from 'services/metadata/types';
import {logError} from 'logging';
import {useLazyGetWorkQuery, useSaveWorkMutation} from 'services/metadata';

/**
 * Fetches work and removes all links.
 *
 * Used when last manifestation is moved away from work, and we dont want
 * links to be present in search (etc) until work deletion in midnight.
 */
export const useCleanupEmptyWorkAfterMove = (workId: WorkV4['id']) => {
  const [getWork] = useLazyGetWorkQuery();
  const [saveWorkMutation] = useSaveWorkMutation();

  const cleanup = useCallback(() => {
    try {
      getWork({id: workId})
        .unwrap()
        .then(({work}) => {
          if (work.links && work.links.length > 0) {
            saveWorkMutation({
              ...work,
              links: [], // Clear all links
            })
              .then(() => {
                console.info('Cleaned up empty work with id:', workId);
              })
              .catch(error => {
                console.error(
                  'Error saving cleaned up work with id:',
                  workId,
                  error,
                );
                throw error;
              });
          } else {
            console.info('No links to clean up for work with id:', workId);
          }
        });
    } catch (error) {
      logError('useCleanupEmptyWorkAfterMove: Error cleaning up empty work', {
        workId,
        error,
      });
    }
  }, [getWork, saveWorkMutation, workId]);

  return {
    clean: cleanup,
  };
};
